import type { ActionTree, Commit } from 'vuex';
import type { manageOnboardingInt, orgFormField } from './OrgOnboardingInt';
import { ONBOARDING_BUY_PLAN, SEND_ORGANIZATION_REQUEST } from './action-types';
import { RESET_FORM, SET_IS_REQUEST_SUCCESS, SET_IS_SUBMIT_INPROGRESS } from './mutation-types';
import OrganizationService from '@/common/services/OrganizationServices';
import { ADD_ERROR_NOTIFICATION } from '@/components/NotificationBar/store/mutation-types';
import i18n from '@/i18n';
import { isNullish } from '@/utils/check-runtime-types';
import { DSPayload, OrganizationInfo } from '@/components/Onboarding/IOnboarding';

function createPayload (organizationInfo: OrganizationInfo, orgId = '') {

  const payload = {
    legalName: organizationInfo.legalName.trim(),
    legalNameTransliterated: organizationInfo.legalNameTransliterated?.trim() || organizationInfo.legalName.trim(),
    headquarterAddress: {
      country: organizationInfo.headquarterAddress.country || '',
      streetName: organizationInfo.headquarterAddress.streetName?.trim() || '',
      streetNumber: organizationInfo.headquarterAddress.streetNumber || '',
      locality: organizationInfo.headquarterAddress.locality?.trim() || '',
      region: organizationInfo.headquarterAddress.region || '',
      postalCode: organizationInfo.headquarterAddress.postalCode || '',
    },
    billingAddress: {
      country: organizationInfo.billingAddress.country || '',
      streetName: organizationInfo.billingAddress.streetName?.trim() || '',
      streetNumber: organizationInfo.billingAddress.streetNumber || '',
      locality: organizationInfo.billingAddress.locality?.trim() || '',
      region: organizationInfo.billingAddress.region || '',
      postalCode: organizationInfo.billingAddress.postalCode || '',
    },
    identifiers: organizationInfo.identifiers || [],
    onboardReqId: orgId,
    contactPerson: {
      firstName: organizationInfo.contactPerson.firstName?.trim() || '',
      lastName: organizationInfo.contactPerson.lastName?.trim() || '',
      phone: `${organizationInfo.contactPerson.phone?.trim() || ''}`,
      email: organizationInfo.contactPerson.email || '',
      language: i18n.global.locale.value.toUpperCase(),
    },
    additionalProperties: {
      bpnNumber: `${organizationInfo.additionalProperties.bpnNumber || ''}`,
    },
    sameAddress: organizationInfo.sameAddress || false,
    isDihUser: false,
  };

  if (orgId.length > 0) {
    payload.onboardReqId = orgId;
  } else {
    payload.isDihUser = true;
  }

  return payload;
}


function handleResponse (commit: Commit, res: any) {
  const { status } = res;
  if (status !== 201) {
    commit(ADD_ERROR_NOTIFICATION, 'genericError');
    throw new Error('error');
  } else {
    commit(SET_IS_REQUEST_SUCCESS, true);
    commit(RESET_FORM);
  }
}

function handleError (commit: Commit, err: any, orgId: string) {
  const { response } = err;
  if (response.status === 409) {
    if (!isNullish(orgId) && orgId.length > 0) {
      commit(ADD_ERROR_NOTIFICATION, response.data);
    }
    throw new Error('proceed');
  } else {
    commit(ADD_ERROR_NOTIFICATION, response.data);
    throw new Error('error');
  }
}

function isOrganizationInfo(
  org: orgFormField | OrganizationInfo
): org is OrganizationInfo {
  return (org as OrganizationInfo).legalName !== undefined;
}


export const actions: ActionTree<manageOnboardingInt, {}> = {
  /**
   * Dispatch the post request to create a new organization
   * @param orgid
   */
  [SEND_ORGANIZATION_REQUEST]({ commit, state }, orgId: string) {
  const { organizationInfo } = state;

  let payload: DSPayload | undefined;
  
  if (isOrganizationInfo(organizationInfo) && orgId?.length > 0) {
    // DS flow
    payload = createPayload(organizationInfo, orgId);
  } else if (isOrganizationInfo(organizationInfo)) {
    // DIH First flow
    payload = createPayload(organizationInfo, orgId);
  }

  commit(SET_IS_SUBMIT_INPROGRESS, true);

  return new Promise((resolve, reject) => {
    OrganizationService.registerNewOrganization(payload)
      .then((res) => {
        handleResponse(commit, res);
        resolve("success");
      })
      .catch((err) => {
        handleError(commit, err, orgId);
        reject("error");
      })
      .finally(() => {
        commit(SET_IS_SUBMIT_INPROGRESS, false);
      });
  });
},


  /**
   * Dispatch post request for buy plan
   * @payload: object : { deviceID: string, planId: string, my dataspace id
   */
  [ONBOARDING_BUY_PLAN] ({ commit }, payload) {
    return new Promise((resolve, reject) => {
      OrganizationService.checkoutPlan(payload)
        .then(() => {
          resolve('success');
        })
        .catch(() => {
          commit(ADD_ERROR_NOTIFICATION, 'genericError');
          reject('error');
        });
    });
  },
};
